import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonSpinner,
  IonTextarea,
  IonToast,
} from "@ionic/react";
import { chevronDownOutline, chevronUpOutline } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import SelectItem from "./SelectItem";
import {
  getInventoryItemsCatalog,
  getInventoryLocationByIdFull,
  getInventoryMovementsForLocation,
  getInventoryOrdersForLocation,
  INVENTORY_HISTORY_PAGE_SIZE,
  postInventoryOrder,
} from "../services/inventoryLocation.service";

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

// Human-readable label for every InventoryMovementType this model can carry
// -- Movement History shows all of them (not just the transfers this
// feature creates), since device installs/returns at farmer plots and
// other backend-driven movement types are real history for a location too.
const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  INTERNAL_TRANSFER: "Stock Transfer",
  INSTALLATION: "Installation",
  RETURNED_TO_INVENTORY: "Returned to Inventory",
  PRODUCTION: "Production",
  ORDER_FULFILLMENT: "Order Fulfillment",
  ORDER_RETURN: "Order Return",
  CONVERTED_TO_RENTAL: "Converted to Rental",
  REPAIR: "Repair",
  TICKET_CONSUMPTION: "Ticket Consumption",
};

// Only INTERNAL_TRANSFER (created by this app's own Move Stock form) always
// has both fromInventoryLocationId and toInventoryLocationId set, so only
// that type gets "Sent to X" / "Received from X" framing. Every other type
// only ever populates one side (it's not a location-to-location transfer),
// so falls back to whatever plot context is on the record, if any.
function movementContextLine(movement: any, locationId: string): string {
  const isFrom = movement.fromInventoryLocationId === locationId;
  if (movement.type === "INTERNAL_TRANSFER") {
    const counterpartyLocation = isFrom ? movement.toInventoryLocation : movement.fromInventoryLocation;
    const counterpartyName = counterpartyLocation?.businessName ?? counterpartyLocation?.name ?? "—";
    return isFrom ? `Sent to ${counterpartyName}` : `Received from ${counterpartyName}`;
  }
  if (movement.plotId) {
    return `Plot ${movement.plotId}`;
  }
  return "";
}

// Current stock snapshot + place-order form + order/movement history for a
// single channel-partner location, shown as the "Orders" tab of
// ChannelPartnerDetail.tsx. `stock`/`order` are loosely-typed
// {inventoryItemId: quantity} maps already living on the InventoryLocation
// record (maintained server-side as a side effect of order create/delete
// and movement processing) -- this component just labels them using the
// (org-wide, unscoped) item catalog. Each concern gets its own IonCard so
// the sections read as distinct blocks rather than one long list.
//
// Order/Movement History use "Load More" buttons rather than
// IonInfiniteScroll -- Ionic's infinite scroll triggers on proximity to the
// bottom of the whole scrollable page, so two independent infinite-scroll
// zones stacked on one page (this one mid-page, movements below it) don't
// coexist reliably: the first would rarely fire since you'd have to scroll
// past the second section first. That pattern only works for a dedicated
// single-list page (see OrderList.tsx). A button sidesteps the conflict
// while still giving each section its own on-demand pagination.
interface Props {
  locationId: string;
}

const LocationOrders: React.FC<Props> = ({ locationId }) => {
  const [items, setItems] = useState<any[]>([]);
  const [stock, setStock] = useState<Record<string, number>>({});
  const [onOrder, setOnOrder] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  const [orders, setOrders] = useState<any[]>([]);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);

  const [movements, setMovements] = useState<any[]>([]);
  const [movementsHasMore, setMovementsHasMore] = useState(false);
  const [loadingMoreMovements, setLoadingMoreMovements] = useState(false);

  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [date, setDate] = useState(todayIsoDate());
  const [comments, setComments] = useState("");
  const [placing, setPlacing] = useState(false);
  const [showSelectItemModal, setShowSelectItemModal] = useState(false);

  const [onSuccess, setOnSuccess] = useState("");
  const [onError, setOnError] = useState("");
  const [showStock, setShowStock] = useState(false);

  function selectOrderItem(item: any) {
    setItemId(item.id);
    setShowSelectItemModal(false);
  }

  function refreshStock() {
    return getInventoryLocationByIdFull(locationId).then((data) => {
      setStock(data?.stock ?? {});
      setOnOrder(data?.order ?? {});
    });
  }

  function refreshOrders() {
    return getInventoryOrdersForLocation(locationId).then((res) => {
      const items = Array.isArray(res) ? res : [];
      setOrders(items);
      setOrdersHasMore(items.length === INVENTORY_HISTORY_PAGE_SIZE);
    });
  }

  function loadMoreOrders() {
    setLoadingMoreOrders(true);
    getInventoryOrdersForLocation(locationId, orders.length)
      .then((res) => {
        const items = Array.isArray(res) ? res : [];
        setOrders((prev) => [...prev, ...items]);
        setOrdersHasMore(items.length === INVENTORY_HISTORY_PAGE_SIZE);
      })
      .catch((err) => setOnError(err?.message ?? String(err)))
      .finally(() => setLoadingMoreOrders(false));
  }

  function refreshMovements() {
    return getInventoryMovementsForLocation(locationId).then((res) => {
      const items = Array.isArray(res) ? res : [];
      setMovements(items);
      setMovementsHasMore(items.length === INVENTORY_HISTORY_PAGE_SIZE);
    });
  }

  function loadMoreMovements() {
    setLoadingMoreMovements(true);
    getInventoryMovementsForLocation(locationId, movements.length)
      .then((res) => {
        const items = Array.isArray(res) ? res : [];
        setMovements((prev) => [...prev, ...items]);
        setMovementsHasMore(items.length === INVENTORY_HISTORY_PAGE_SIZE);
      })
      .catch((err) => setOnError(err?.message ?? String(err)))
      .finally(() => setLoadingMoreMovements(false));
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getInventoryItemsCatalog().then((res) => setItems(Array.isArray(res) ? res : [])),
      refreshStock(),
      refreshOrders(),
      refreshMovements(),
    ])
      .catch((err) => setOnError(err?.message ?? String(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  function itemName(id: string) {
    return items.find((it) => it.id === id)?.name ?? id;
  }

  function placeOrder() {
    if (!itemId || !quantity || Number(quantity) <= 0) {
      setOnError("Select an item and enter a quantity greater than 0");
      return;
    }
    setPlacing(true);
    postInventoryOrder({
      // The backend's `date` property is type 'date' and validates as full
      // ISO date-time -- the plain "YYYY-MM-DD" from <input type="date">
      // fails that format check (422 UnprocessableEntityError).
      date: new Date(date).toISOString(),
      quantity: Number(quantity),
      inventoryLocationId: locationId,
      inventoryItemId: itemId,
      comments: comments || undefined,
    })
      .then(() => {
        setOnSuccess("Order placed");
        setItemId("");
        setQuantity("");
        setComments("");
        return Promise.all([refreshStock(), refreshOrders()]);
      })
      .catch((err: any) => setOnError(err?.message ?? String(err)))
      .finally(() => setPlacing(false));
  }

  if (loading) {
    return <IonSpinner name="bubbles" />;
  }

  // Only items with a recorded stock/order history at this location -- the
  // full catalog mixes in a lot of unrelated hardware/device items (with
  // no location tie), so showing all of them here was mostly noise.
  const stockedItems = items.filter((item) => stock[item.id] !== undefined || onOrder[item.id] !== undefined);

  return (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Place Order</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          <IonList>
            <IonItem>
              <IonLabel position="stacked">Item</IonLabel>
              <IonInput
                readonly
                value={items.find((item) => item.id === itemId)?.name ?? ""}
                placeholder="Select item"
                onClick={() => setShowSelectItemModal(true)}
              ></IonInput>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Quantity</IonLabel>
              <IonInput
                type="number"
                min="1"
                value={quantity}
                onIonChange={(e) => setQuantity(e.detail.value ?? "")}
              ></IonInput>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Date</IonLabel>
              <IonInput type="date" value={date} onIonChange={(e) => setDate(e.detail.value ?? todayIsoDate())}></IonInput>
            </IonItem>

            <IonItem lines="none">
              <IonLabel position="stacked">Comments</IonLabel>
              <IonTextarea value={comments} onIonChange={(e) => setComments(e.detail.value ?? "")}></IonTextarea>
            </IonItem>
          </IonList>

          <IonButton expand="block" disabled={placing} onClick={placeOrder}>
            {placing ? <IonSpinner name="bubbles" /> : "Place Order"}
          </IonButton>
        </IonCardContent>
      </IonCard>

      <IonCard>
        <IonItem button lines={showStock ? "full" : "none"} detail={false} onClick={() => setShowStock(!showStock)}>
          <IonLabel>
            <strong>Current Stock {stockedItems.length > 0 && `(${stockedItems.length})`}</strong>
          </IonLabel>
          <IonIcon slot="end" icon={showStock ? chevronUpOutline : chevronDownOutline} />
        </IonItem>
        {showStock && (
          <IonCardContent>
            {stockedItems.length === 0 && <p className="secondary-text">No stock recorded yet for this location.</p>}
            <IonList>
              {stockedItems.map((item) => (
                <IonItem key={item.id} lines="full">
                  <IonLabel>
                    <h2>{item.name}</h2>
                  </IonLabel>
                  <div slot="end" className="ion-text-end">
                    <IonNote style={{ display: "block" }}>{stock[item.id] ?? 0} in stock</IonNote>
                    <IonNote style={{ display: "block", marginTop: 2 }} class="secondary-text">
                      {onOrder[item.id] ?? 0} on order
                    </IonNote>
                  </div>
                </IonItem>
              ))}
            </IonList>
          </IonCardContent>
        )}
      </IonCard>

      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Order History</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {orders.length === 0 && <p className="secondary-text">No orders placed yet.</p>}
          <IonList>
            {orders.map((order) => (
              <IonItem key={order.id} lines="full">
                <IonLabel>
                  <h2>{order.inventoryItem?.name ?? itemName(order.inventoryItemId)}</h2>
                  <p>{order.date?.slice(0, 10)}</p>
                  {order.comments && <p className="secondary-text">{order.comments}</p>}
                </IonLabel>
                <IonNote slot="end" class="ion-text-end">
                  Qty {order.quantity}
                </IonNote>
              </IonItem>
            ))}
          </IonList>
          {ordersHasMore && (
            <IonButton expand="block" fill="outline" disabled={loadingMoreOrders} onClick={loadMoreOrders}>
              {loadingMoreOrders ? <IonSpinner name="bubbles" /> : "Load More"}
            </IonButton>
          )}
        </IonCardContent>
      </IonCard>

      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Movement History</IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {movements.length === 0 && <p className="secondary-text">No movements recorded yet.</p>}
          <IonList>
            {movements.map((movement) => {
              const contextLine = movementContextLine(movement, locationId);
              return (
                <IonItem key={movement.id} lines="full">
                  <IonLabel>
                    <h2>{movement.inventoryItem?.name ?? itemName(movement.inventoryItemId)}</h2>
                    <p>{movement.date?.slice(0, 10)}</p>
                    {contextLine && <p className="secondary-text">{contextLine}</p>}
                    {movement.comments && <p className="secondary-text">{movement.comments}</p>}
                  </IonLabel>
                  <div slot="end" className="ion-text-end">
                    <IonBadge color="medium">{MOVEMENT_TYPE_LABELS[movement.type] ?? movement.type ?? "Movement"}</IonBadge>
                    <IonNote style={{ display: "block", marginTop: 4 }}>Qty {movement.quantity}</IonNote>
                  </div>
                </IonItem>
              );
            })}
          </IonList>
          {movementsHasMore && (
            <IonButton expand="block" fill="outline" disabled={loadingMoreMovements} onClick={loadMoreMovements}>
              {loadingMoreMovements ? <IonSpinner name="bubbles" /> : "Load More"}
            </IonButton>
          )}
        </IonCardContent>
      </IonCard>

      <IonToast isOpen={!!onSuccess} onDidDismiss={() => setOnSuccess("")} message={onSuccess} duration={2000} color="success" />
      <IonToast isOpen={!!onError} onDidDismiss={() => setOnError("")} message={onError} duration={2500} color="danger" />

      {showSelectItemModal && (
        <IonModal isOpen={showSelectItemModal}>
          <SelectItem
            label="Select Item"
            items={items}
            labelKey="name"
            valueKey="id"
            setItem={selectOrderItem}
            selectedValue={itemId}
            onClose={setShowSelectItemModal}
          />
        </IonModal>
      )}
    </>
  );
};

export default LocationOrders;
